import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { CalendarIcon, HelpCircle, Package } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { TimePicker } from "@/components/ui/time-picker";
import { ScrollablePicker } from "@/components/ui/scrollable-picker";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PackagePickupLog } from "@/types/packagePickup";
import { useCreatePackagePickupLog, useUpdatePackagePickupLog } from "@/hooks/usePackagePickupQueries";

const formSchema = z.object({
  pickup_date: z.date({
    required_error: "Selecteer een datum voor de ophaling",
  }).refine((date) => {
    const day = date.getDay();
    return day >= 1 && day <= 5; // Monday = 1, Friday = 5
  }, {
    message: "Alleen werkdagen (maandag t/m vrijdag) zijn toegestaan",
  }),
  pickup_time: z.string()
    .min(1, "Selecteer een tijd voor de ophaling")
    .regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Tijd moet in het formaat UU:MM zijn (bijv. 14:30)")
    .refine((time) => {
      const [hours] = time.split(':').map(Number);
      return hours >= 20 && hours <= 23;
    }, "Ophaaltijd moet tussen 20:00 en 23:00 zijn"),
  aantal_rolcontainers: z.number({
    required_error: "Aantal rolcontainers is verplicht",
    invalid_type_error: "Voer een geldig aantal in",
  })
    .min(0, "Aantal rolcontainers moet minimaal 0 zijn")
    .max(15, "Aantal rolcontainers mag maximaal 15 zijn")
    .int("Aantal rolcontainers moet een heel getal zijn"),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface PackagePickupFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingLog?: PackagePickupLog | null;
  initialDate?: string;
}

const PackagePickupForm = ({ open, onOpenChange, editingLog, initialDate }: PackagePickupFormProps) => {
  const createMutation = useCreatePackagePickupLog();
  const updateMutation = useUpdatePackagePickupLog();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      pickup_date: initialDate ? new Date(initialDate) : new Date(),
      pickup_time: editingLog?.pickup_time || "",
      aantal_rolcontainers: editingLog?.aantal_rolcontainers || 0,
      notes: editingLog?.notes || "",
    },
  });

  // Reset form when dialog opens/closes or editing log changes
  React.useEffect(() => {
    if (open) {
      form.reset({
        pickup_date: editingLog 
          ? new Date(editingLog.pickup_date) 
          : (initialDate ? new Date(initialDate) : new Date()),
        pickup_time: editingLog?.pickup_time || "",
        aantal_rolcontainers: editingLog?.aantal_rolcontainers || 0,
        notes: editingLog?.notes || "",
      });
    }
  }, [open, editingLog, initialDate, form]);

  const onSubmit = async (data: FormData) => {
    try {
      const formattedData = {
        pickup_date: format(data.pickup_date, "yyyy-MM-dd"),
        pickup_time: data.pickup_time,
        aantal_rolcontainers: data.aantal_rolcontainers,
        notes: data.notes || undefined,
      };

      if (editingLog) {
        await updateMutation.mutateAsync({
          id: editingLog.id,
          data: {
            pickup_time: formattedData.pickup_time,
            aantal_rolcontainers: formattedData.aantal_rolcontainers,
            notes: formattedData.notes,
          },
        });
      } else {
        await createMutation.mutateAsync(formattedData);
      }

      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the mutation hooks
      console.error("Form submission error:", error);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              {editingLog ? "Ophaaltijd Bijwerken" : "Nieuwe Ophaaltijd Registreren"}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="pickup_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="flex items-center gap-2">
                      Datum *
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Selecteer de datum waarop de pakketten worden opgehaald</p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "pl-3 text-left font-normal transition-colors",
                              !field.value && "text-muted-foreground",
                              "hover:bg-accent hover:text-accent-foreground"
                            )}
                            disabled={!!editingLog} // Disable date change when editing
                          >
                            {field.value ? (
                              format(field.value, "EEEE d MMMM yyyy", { locale: nl })
                            ) : (
                              <span>Selecteer datum</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={nl}
                          disabled={(date) => {
                            const day = date.getDay();
                            return day === 0 || day === 6 || date < new Date(new Date().setDate(new Date().getDate() - 30));
                          }}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pickup_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Ophaaltijd *
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Tijd moet tussen 20:00 en 23:00 zijn. Gebruik 24-uurs formaat.</p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <FormControl>
                      <TimePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Selecteer ophaaltijd"
                      />
                    </FormControl>
                    <FormDescription>
                      Selecteer de tijd waarop de pakketten worden opgehaald (avondophaling: 20:00-23:00)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="aantal_rolcontainers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Aantal Rolcontainers *
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Selecteer het aantal rolcontainers dat wordt opgehaald (0-15)</p>
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <FormControl>
                      <ScrollablePicker
                        value={field.value}
                        onChange={field.onChange}
                        min={0}
                        max={15}
                        placeholder="Selecteer aantal containers"
                        icon={<Package className="h-4 w-4 text-muted-foreground" />}
                        label="containers"
                      />
                    </FormControl>
                    <FormDescription>
                      Selecteer het aantal rolcontainers met de scroll-kiezer (tussen 0 en 15)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opmerkingen</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Bijzonderheden over de ophaling, zoals vertraging, extra containers, etc."
                        className="resize-none min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Optioneel: voeg eventuele bijzonderheden toe
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="min-w-[100px]"
                  disabled={isLoading}
                >
                  Annuleren
                </Button>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="min-w-[120px]"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                      Bezig...
                    </div>
                  ) : (
                    editingLog ? "Bijwerken" : "Registreren"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default PackagePickupForm;